import { useEffect, useRef } from "react";

type UseBrowserBackInterceptionProps = {
  currentStep: number;
  productPageUrl: string;
};

/**
 * Intercepts browser back button to prevent leaving the creation flow.
 *
 * When the user clicks the browser back button, this hook:
 * 1. Prevents navigation away from the creation flow
 * 2. Fires a custom "creationFlowBrowserBack" event
 * 3. Navigates to product page ONLY if event.preventDefault() is NOT called
 *
 * @example
 * // To block navigation and do custom logic:
 * useEffect(() => {
 *   const handleBack = (event: Event) => {
 *     event.preventDefault(); // BLOCK navigation
 *     // Do your custom logic
 *   };
 *   window.addEventListener('creationFlowBrowserBack', handleBack);
 *   return () => window.removeEventListener('creationFlowBrowserBack', handleBack);
 * }, []);
 */
export const useBrowserBackInterception = ({
  currentStep,
  productPageUrl,
}: UseBrowserBackInterceptionProps) => {
  const currentStepRef = useRef(currentStep);
  const productPageUrlRef = useRef(productPageUrl);

  // Keep refs updated with latest values
  useEffect(() => {
    currentStepRef.current = currentStep;
  }, [currentStep]);

  useEffect(() => {
    productPageUrlRef.current = productPageUrl;
  }, [productPageUrl]);

  // Prevent browser back from leaving creation flow and allow custom logic
  useEffect(() => {
    // Push a guard entry when entering creation flow (only once on mount)
    window.history.pushState(
      { inCreationFlow: true },
      "",
      window.location.href
    );

    const handlePopState = (event: PopStateEvent) => {
      // User clicked back, which would navigate away from creation flow
      // Immediately push forward again to cancel the navigation
      window.history.pushState(
        { inCreationFlow: true },
        "",
        window.location.href
      );

      // Fire custom event for user's logic
      const browserBackEvent = new CustomEvent("creationFlowBrowserBack", {
        detail: {
          currentStep: currentStepRef.current,
          productPageUrl: productPageUrlRef.current,
        },
        bubbles: true,
        cancelable: true,
      });
      const shouldNavigateBack = window.dispatchEvent(browserBackEvent);

      // If event wasn't prevented, navigate back to product page
      if (shouldNavigateBack) {
        window.location.href = productPageUrlRef.current;
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);
};
